/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */


import smoothscroll from 'smoothscroll-polyfill';
smoothscroll.polyfill();
// Import all the third party stuff
import React from 'react';
import { createRoot } from 'react-dom/client';

import loadable from '@loadable/component';
import { Provider } from 'react-redux';
import { applyRouterMiddleware, Router, browserHistory } from 'react-router';
import { syncHistoryWithStore } from 'react-router-redux';
import { selectLocationState } from 'containers/App/selectors';

import configureStore from './store';

import getFrontendHost from './getFrontendHost';



// Create redux store with history
// this uses the singleton browserHistory provided by react-router
// Optionally, this could be changed to leverage a created history
// e.g. `const browserHistory = useRouterHistory(createBrowserHistory)();`
const initialState = {};
const store = configureStore(initialState, browserHistory);


// Sync history and store, as the react-router-redux reducer
// is under the non-default key ('routing'), selectLocationState
// must be provided for resolving how to retrieve the 'route' in the state

const history = syncHistoryWithStore(browserHistory, store, {
 selectLocationState: selectLocationState(),
});

// Set up the router, wrapping all Routes in the App component


// const App = React.lazy(() => import('containers/App/index.js'));
// const PublicViewEvent = React.lazy(() => import('containers/PublicViewEvent'));

const App = loadable(() => import('containers/App/index.js'));
const Dashboard = loadable(() => import('containers/Dashboard/index.js'));
const AppGenerated = loadable(() => import('containers/AppGenerated/index.js'));
// const PublicViewEvent = loadable(() => import('containers/PublicViewEvent'));
// import App from 'containers/App';
// import PublicViewEvent from 'containers/PublicViewEvent';

// import createRoutes from './routes';
// const rootRoute = {
//   component: PublicViewEvent,
//   childRoutes: createRoutes(store),
// };

// See: https://github.com/styled-components/styled-components/issues/1593#issuecomment-409011695

const MOUNT_NODE = document.getElementById('app');

function appContainer() {

 const acaraDomains = process.env.ACARA_DEPLOY_ENV === 'production' ? [getFrontendHost(false, true), 'www.app.acara.io', 'app.acara.io'] : [getFrontendHost(false, true), 'staging.acara.io', 'www.staging.acara.io'];

 const getCurrentLocation = history.getCurrentLocation().pathname.split('/')[1];

 const currentDomain = window.location.hostname;

 if ('dashboard' === getCurrentLocation) {
  return Dashboard;
 } else if (acaraDomains.includes(currentDomain) || window.location.hostname === 'localhost') {
   return App;
 }
 return AppGenerated;
}


async function appStore() {

 const acaraDomains = process.env.ACARA_DEPLOY_ENV === 'production' ? [getFrontendHost(false, true), 'www.app.acara.io', 'app.acara.io'] : [getFrontendHost(false, true), 'staging.acara.io', 'www.staging.acara.io'];
 const getCurrentLocation = history.getCurrentLocation().pathname.split('/')[1];  

 const currentDomain = window.location.hostname;
 
 if ('dashboard' === getCurrentLocation) {
   return await import('./dashboardRoutes.js');
 } else if (acaraDomains.includes(currentDomain) || window.location.hostname === 'localhost')  {
   return await import('./createRoutes.js');
 } else {
   return await import('./generatorRoutes.js');
 }
 
}




const render = async (translatedMessages) => {



 const App = appContainer();

 const {
   generatorRoutes,
   dashboardRoutes,
   createRoutes
 } = await appStore();

 const rootRoute = {
   component: App,
   childRoutes: dashboardRoutes ? dashboardRoutes(store) : generatorRoutes ? generatorRoutes(store) : createRoutes(store),
 };

 return createRoot(
   MOUNT_NODE
 ).render(
   <Provider store={store}>
     <Router
       history={history}
       routes={rootRoute}
       render={
         // Scroll to top when going to a new page, imitating default browser
         // behaviour
         applyRouterMiddleware()
       }
     />
   </Provider>
 );
};

// Hot reloadable translation json files

if (import.meta.webpackHot) {
  import.meta.webpackHot.accept(['containers/App'], () => {
    render.unmount();
    render();
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
 new Promise(resolve => {
   resolve(import('intl'));
 })
   .then(() =>
     Promise.all([
       import('intl/locale-data/jsonp/en.js'),
     ]),
   ) // eslint-disable-line prettier/prettier
   .then(() => render())
   .catch(err => {
     throw err;
   });
} else {
 render();
}

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed

if (process.env.ACARA_DEPLOY_ENV === 'production' && window.LogRocket) {
 window.LogRocket.init('acara-main/acara');
}